import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { HomeIcon, KeyRoundIcon, UsersIcon } from 'lucide-react';
import { StepFrame, FieldGrid, Callout } from '../StepFrame';
import { ChoiceCard } from '../../ui/ChoiceCard';
import { HelpPanel } from '../../ui/HelpPanel';
import { TextField } from '../../ui/TextField';
import { SsnField } from '../../ui/SsnField';
import { usePortal } from '../../../contexts/PortalContext';
import { formatAmountInput, parseAmount } from '../../../utils/format';
export function NameStep() {
  const {
    answers,
    update
  } = usePortal();
  return <StepFrame title="What’s your legal name?" description="Exactly as it appears on your driver’s license or passport." help={<HelpPanel question="Why does it need to match my ID?" data-id="element-87" data-mp="1ln4p5w6k6pff">
          Your name has to match across your credit report, ID, and closing documents. Small differences — like a missing middle name — can delay closing.
        </HelpPanel>} data-id="element-86" data-mp="1goba3zbmiqd5">
      <FieldGrid data-id="element-88" data-mp="pb513wc46xf4">
        <TextField label="First name" value={answers.firstName} onChange={v => update({
        firstName: v
      })} autoComplete="given-name" data-id="element-89" data-mp="pan0knif5x1e" />
        <TextField label="Middle name" optional value={answers.middleName} onChange={v => update({
        middleName: v
      })} autoComplete="additional-name" data-id="element-90" data-mp="kahfxrakhast" />
        <TextField label="Last name" value={answers.lastName} onChange={v => update({
        lastName: v
      })} autoComplete="family-name" data-id="element-91" data-mp="u250r9cmbtlb" />
        <TextField label="Suffix" optional placeholder="Jr., III" value={answers.suffix} onChange={v => update({
        suffix: v
      })} data-id="element-92" data-mp="6d2l271joatg" />
      </FieldGrid>
    </StepFrame>;
}
export function ContactStep() {
  const {
    answers,
    update
  } = usePortal();
  return <StepFrame title="Contact and identity" description="We’ll only text you about your loan — never marketing." help={<HelpPanel question="Why do you need my Social Security number?" data-id="element-94" data-mp="1qqt3yzjodcao">
          It verifies your identity and, only after you give permission on the credit step, lets us check your credit. You enter it once here — we never ask for it again.
        </HelpPanel>} data-id="element-93" data-mp="adw5urjox9i4">
      <div className="space-y-4" data-id="element-95" data-mp="oe6kzhlrgtlv">
        <TextField label="Email" type="email" value={answers.email} onChange={v => update({
        email: v
      })} autoComplete="email" data-id="element-96" data-mp="ohccf3hv47kr" />
        <TextField label="Mobile phone" type="tel" value={answers.phone} onChange={v => update({
        phone: v
      })} autoComplete="tel" data-id="element-97" data-mp="xfnzwwnfmp8z" />
        <TextField label="Date of birth" type="date" value={answers.dob} onChange={v => update({
        dob: v
      })} autoComplete="bday" error={!answers.dob ? 'Needed to verify your identity' : undefined} data-id="element-98" data-mp="qov5rm245lj" />
        <SsnField value={answers.ssn} onChange={v => update({
        ssn: v
      })} error={answers.ssn.length !== 9 ? 'Needed to verify your identity' : undefined} data-id="element-99" data-mp="egsarzqfl120" />
      </div>
    </StepFrame>;
}
export function StatusStep() {
  const {
    answers,
    update
  } = usePortal();
  return <StepFrame title="A couple of quick details" help={<>
          <HelpPanel question="Why do you ask about marital status?" data-id="element-101" data-mp="3akjmio2oghul">
            In some states, a spouse may need to sign certain closing documents even if they aren’t on the loan. “Unmarried” includes single, divorced, and widowed.
          </HelpPanel>
          <HelpPanel question="Does citizenship affect my loan?" data-id="element-102" data-mp="2wnmoo2o56047">
            Permanent and many non-permanent residents qualify for mortgages. We ask so we can match you with the right loan programs.
          </HelpPanel>
        </>} data-id="element-100" data-mp="3i3slrohgz05x">
      <fieldset data-id="element-103" data-mp="2muyrh6t91u4y">
        <legend className="mb-3 text-sm font-medium text-ink-700" data-id="element-104" data-mp="2hxyjurgr4jbq">Marital status</legend>
        <div role="radiogroup" className="grid gap-2.5 sm:grid-cols-3" data-id="element-105" data-mp="2hvm3ujuk0j1b">
          {(['married', 'separated', 'unmarried'] as const).map(v => <ChoiceCard key={v} title={v[0].toUpperCase() + v.slice(1)} selected={answers.maritalStatus === v} onSelect={() => update({
          maritalStatus: v
        })} data-id="element-106" data-mp="28w8s1kiyjidf" />)}
        </div>
      </fieldset>
      <fieldset className="mt-8" data-id="element-107" data-mp="7w3vpxaqi5dv">
        <legend className="mb-3 text-sm font-medium text-ink-700" data-id="element-108" data-mp="7vudivvxl2iq">Citizenship</legend>
        <div role="radiogroup" className="grid gap-2.5" data-id="element-109" data-mp="bw4o6luo4h3x">
          <ChoiceCard title="U.S. citizen" selected={answers.citizenship === 'citizen'} onSelect={() => update({
          citizenship: 'citizen'
        })} data-id="element-110" data-mp="3ub5h9lnwtj71" />
          <ChoiceCard title="Permanent resident" description="You have a green card." selected={answers.citizenship === 'permanent'} onSelect={() => update({
          citizenship: 'permanent'
        })} data-id="element-111" data-mp="32rhmh3olgzd" />
          <ChoiceCard title="Non-permanent resident" description="For example, an H-1B or L-1 visa." selected={answers.citizenship === 'nonpermanent'} onSelect={() => update({
          citizenship: 'nonpermanent'
        })} data-id="element-112" data-mp="3pcc3ds4pnq6c" />
        </div>
      </fieldset>
    </StepFrame>;
}
export function HomeAddressStep() {
  const {
    answers,
    update
  } = usePortal();
  const needsPrevious = answers.yearsAtAddress !== '' && parseAmount(answers.yearsAtAddress) < 2;
  return <StepFrame title="Where do you live now?" data-id="element-113" data-mp="1onfw50th1p9k">
      <div className="space-y-4" data-id="element-114" data-mp="1oo7n1yc4iyti">
        <TextField label="Street address" value={answers.currentStreet} onChange={v => update({
        currentStreet: v
      })} autoComplete="street-address" data-id="element-115" data-mp="1ol1yqlbbk0yu" />
        <div className="grid grid-cols-6 gap-4" data-id="element-116" data-mp="1xgrqrd79vtl7">
          <TextField className="col-span-6 sm:col-span-3" label="City" value={answers.currentCity} onChange={v => update({
          currentCity: v
        })} data-id="element-117" data-mp="1xhku0470w0yl" />
          <TextField className="col-span-2 sm:col-span-1" label="State" value={answers.currentState} onChange={v => update({
          currentState: v
        })} maxLength={2} data-id="element-118" data-mp="27cuv8pplk4bb" />
          <TextField className="col-span-4 sm:col-span-2" label="ZIP" value={answers.currentZip} onChange={v => update({
          currentZip: v
        })} data-id="element-119" data-mp="2gem1bvo1ud5p" />
        </div>
        <FieldGrid data-id="element-120" data-mp="2cdihky2vrwz7">
          <TextField label="How long have you lived here?" inputMode="decimal" suffix="years" value={answers.yearsAtAddress} onChange={v => update({
          yearsAtAddress: v.replace(/[^0-9.]/g, '')
        })} data-id="element-121" data-mp="2cawa0yfzphgz" />
        </FieldGrid>
      </div>
      <AnimatePresence initial={false} data-id="element-122" data-mp="2h3zir1fxijpo">
        {needsPrevious && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.25,
        ease: [0.23, 1, 0.32, 1]
      }} className="overflow-hidden" data-id="element-123" data-mp="2h5u24bbjy3rn">
            <div className="space-y-4 pt-8" data-id="element-124" data-mp="2w0qkhhd2s7ig">
              <Callout data-id="element-125" data-mp="2w1rvxmy06w0p">Since you’ve been here less than 2 years, we also need your previous address.</Callout>
              <TextField label="Previous street address" value={answers.prevStreet} onChange={v => update({
            prevStreet: v
          })} data-id="element-126" data-mp="3022ahnzv2ur6" />
              <div className="grid grid-cols-6 gap-4" data-id="element-127" data-mp="34zc2nbemxkip">
                <TextField className="col-span-6 sm:col-span-3" label="City" value={answers.prevCity} onChange={v => update({
              prevCity: v
            })} data-id="element-128" data-mp="39tpy1dtarrnd" />
                <TextField className="col-span-2 sm:col-span-1" label="State" value={answers.prevState} onChange={v => update({
              prevState: v
            })} maxLength={2} data-id="element-129" data-mp="3jqkxbyxe9w2p" />
                <TextField className="col-span-4 sm:col-span-2" label="ZIP" value={answers.prevZip} onChange={v => update({
              prevZip: v
            })} data-id="element-130" data-mp="3nspkqfx91wgw" />
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </StepFrame>;
}
export function HousingStep() {
  const {
    answers,
    update
  } = usePortal();
  return <StepFrame title="Do you rent or own where you live now?" data-id="element-131" data-mp="y5bcs5j4lv61">
      <div role="radiogroup" className="grid gap-3" data-id="element-132" data-mp="y6w5rqpsl61s">
        <ChoiceCard icon={<KeyRoundIcon className="h-5 w-5" data-id="element-134" data-mp="1vrsb9e3lietd" />} title="I rent" selected={answers.housing === 'rent'} onSelect={() => update({
        housing: 'rent'
      })} data-id="element-133" data-mp="1vt3bj3xu14fq" />
        <ChoiceCard icon={<HomeIcon className="h-5 w-5" data-id="element-136" data-mp="1m2qtbidhpncr" />} title="I own" selected={answers.housing === 'own'} onSelect={() => update({
        housing: 'own'
      })} data-id="element-135" data-mp="1qw368v8t45po" />
        <ChoiceCard icon={<UsersIcon className="h-5 w-5" data-id="element-138" data-mp="1h3nmpthv392o" />} title="I live rent-free" description="For example, with family." selected={answers.housing === 'free'} onSelect={() => update({
        housing: 'free'
      })} data-id="element-137" data-mp="1h34fb6pu832w" />
      </div>
      <AnimatePresence initial={false} data-id="element-139" data-mp="29ra2s18a066f">
        {answers.housing === 'rent' && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.22
      }} className="overflow-hidden" data-id="element-140" data-mp="1i3gsgv10s02j">
            <div className="pt-6 sm:w-1/2" data-id="element-141" data-mp="195mvdhvfl2eb">
              <TextField label="Monthly rent" prefix="$" inputMode="numeric" value={formatAmountInput(answers.rent)} onChange={v => update({
            rent: v.replace(/[^0-9]/g, '')
          })} data-id="element-142" data-mp="1d5x3b02sa2vt" />
            </div>
          </motion.div>}
      </AnimatePresence>
    </StepFrame>;
}